import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, publishReplay, refCount, catchError, switchMap } from 'rxjs/operators';
import { ErrorLevel } from 'src/app/models/enums';
import { ICustomNotification } from 'src/app/models/notification/customNotification.interface';
import { LoaderService } from 'src/app/services/loader.service';
import { NotificationService } from 'src/app/services/notification.service';
import { BaseService } from 'src/app/services/providers/base.service';
import { UserActivityService } from 'src/app/services/userActivity.service';
import { StorageService } from 'src/app/services/util/storage.service';
import { DynamicTranslations } from 'src/locale/dynamicTranslations';
import { IBalance } from '../../models/balance.interface';
import { IBalanceTO } from '../../models/balanceTO.interface';
import { IBaseResponse } from '../../models/baseResponse.interface';
import { ITransferBalanceAmountRequest } from '../../models/requests/transferBalanceAmountRequest.interface';
import { ITransferBalanceAmountResponse } from '../../models/response/transferBalanceAmountResponse.interface';
import { IShop } from '../../models/shop.interface';
import { IAffiliate } from '../../models/user/affiliate.interface';

@Injectable({
  providedIn: 'root'
})
export class ShopProviderService extends BaseService {



  constructor(
    private http: HttpClient,
    protected loaderService: LoaderService,
    protected userActivityService: UserActivityService,
    protected notificationService: NotificationService,
    protected storage: StorageService) {
    super(loaderService, userActivityService, notificationService, storage);
  }

  getAllShopsForAffiliateId(id: number): Observable<IShop[]> {
    const url = this.tcketManagerBaseUrl + 'shop/getAllShopsForAffiliateId' + '/' + id;

    const params = new HttpParams();

    return this.http.get<IBaseResponse>(url).pipe(
      switchMap((response) => {
        if (response?.isSuccess) {
          return of(response.data as IShop[]);
        } else {
          this.openErrorNotification(response.failureReason);
          return of(null);
        }
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        console.log(err);
        return [];
      }),
    );
  }

  getShop(id: number): Observable<IShop> {
    const url = this.tcketManagerBaseUrl + 'shop/getShopById' + '/' + id;

    const params = new HttpParams();

    return this.http.get<IBaseResponse>(url).pipe(
      switchMap((response) => {
        if (response?.isSuccess) {
          return of(response.data as IShop);
        } else {
          this.openErrorNotification(response.failureReason);
          return of(null);
        }
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        console.log(err);
        return [];
      }),
    );
  }

  createShop(shop: IShop): Observable<IShop> {
    const params = new HttpParams();
    const body = JSON.stringify(shop);

    return this.http.post<IBaseResponse>(`${this.tcketManagerBaseUrl}shop/createShop`, body, { headers: this.httpHeaders, params })
      .pipe(
        switchMap((response) => {
          if (response?.isSuccess) {
            return of(response.data as IShop);
          } else {
            this.openErrorNotification(response.failureReason);
            return of(null);
          }
        }),
        catchError(error => of(null)));
  }

  updateShop(shop: IShop): Observable<IShop> {
    const params = new HttpParams();
    const body = JSON.stringify({
      id: shop.id,
      affiliateId: shop.affiliateId,
      name: shop.name,
      state: shop.state,
      city: shop.city,
      address: shop.address,
      dateUpdated: ((new Date()).toISOString().replace('Z', ''))
    });

    return this.http.post<IBaseResponse>(`${this.tcketManagerBaseUrl}shop/updateShop`, body, { headers: this.httpHeaders, params })
      .pipe(
        switchMap((response) => {
          if (response?.isSuccess) {
            return of(response.data);
          } else {
            this.openErrorNotification(response.failureReason);
            return of (null);
          }
        }),
        catchError(error => {
          this.handleError(error, true, true);
          return throwError(error);
        }));
  }

  deleteShop(shopId: number): Observable<boolean> {
    const params = new HttpParams();

    return this.http.delete<IBaseResponse>(`${this.tcketManagerBaseUrl}shop/deleteShop/` + shopId, { headers: this.httpHeaders, params })
      .pipe(
        switchMap((response) => {
          if (response?.isSuccess) {
            return of(true);
          } else {
            this.openErrorNotification(response.failureReason);
            throwError(response.failureReason);
            return of(false);
          }
        }),
        catchError(error => {
          this.handleError(error, true, true);
          return of(null);
        }));
  }
}
